/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import apiClient from './client';
import type { APIResponse } from '@/types';

/**
 * Additional contact phones / e-mails for any user — driver or owner.
 *
 * Backend route is "/user-contacts" WITHOUT an api/ prefix, so these calls ride the catch-all
 * /api proxy (setupProxy.js in dev, server.js in prod). A backend route of "api/user-contacts"
 * would need its own explicit Express proxy entry and 404 without one.
 */

export interface UserContactItem {
  /** Absent on the primary row — the primary contact lives on the Users record and is not editable here. */
  id?: string;
  /** Normalized phone (E.164) or lower-cased e-mail. */
  value: string;
  label?: string;
  /** "primary" | "manual" | "linkSend" */
  source?: string;
  isPrimary: boolean;
}

export interface UserContacts {
  userId: string;
  primaryPhone?: string;
  primaryEmail?: string;
  /** Primary first, then the additional entries oldest-first. */
  phones: UserContactItem[];
  emails: UserContactItem[];
}

const EMPTY: UserContacts = { userId: '', phones: [], emails: [] };

export const userContactsApi = {
  get: async (userId: string): Promise<UserContacts> => {
    if (!userId) return EMPTY;
    const res = await apiClient.get<APIResponse<UserContacts>>(`/user-contacts/${userId}`);
    return res.data?.result ?? EMPTY;
  },

  addPhone: async (userId: string, value: string, label?: string): Promise<UserContactItem> => {
    const res = await apiClient.post<APIResponse<UserContactItem>>(`/user-contacts/${userId}/phones`, { value, label });
    return res.data.result;
  },

  addEmail: async (userId: string, value: string, label?: string): Promise<UserContactItem> => {
    const res = await apiClient.post<APIResponse<UserContactItem>>(`/user-contacts/${userId}/emails`, { value, label });
    return res.data.result;
  },

  updatePhone: async (id: string, value: string, label?: string): Promise<UserContactItem> => {
    const res = await apiClient.put<APIResponse<UserContactItem>>(`/user-contacts/phones/${id}`, { value, label });
    return res.data.result;
  },

  updateEmail: async (id: string, value: string, label?: string): Promise<UserContactItem> => {
    const res = await apiClient.put<APIResponse<UserContactItem>>(`/user-contacts/emails/${id}`, { value, label });
    return res.data.result;
  },

  deletePhone: async (id: string): Promise<void> => {
    await apiClient.delete(`/user-contacts/phones/${id}`);
  },

  deleteEmail: async (id: string): Promise<void> => {
    await apiClient.delete(`/user-contacts/emails/${id}`);
  },

  /**
   * File a phone / e-mail typed while sending a link so the next send offers it. The backend
   * ignores blanks, the user's primary contact and anything already on file. Fire-and-forget:
   * the link has already gone out, so a bookkeeping failure must not surface as an error.
   */
  remember: async (userId: string, contacts: { phone?: string; email?: string }): Promise<void> => {
    if (!userId || (!contacts.phone && !contacts.email)) return;
    try {
      await apiClient.post(`/user-contacts/${userId}/remember`, contacts);
    } catch (err) {
      console.warn('Could not remember contact for user', userId, err);
    }
  },
};
